// 中文文案集中处（模块 10 DoD）。错误码文案见 `errorText`。

export const zh = {
  app: { name: "PrimeBot 控制台" },
  common: {
    loading: "加载中…",
    empty: "暂无数据",
    retry: "重试",
    save: "保存",
    cancel: "取消",
    confirm: "确定",
    create: "新建",
    delete: "删除",
    edit: "编辑",
    close: "关闭",
    search: "搜索",
    all: "全部",
    loadMore: "加载更多",
    noMore: "没有更多了",
    yes: "是",
    no: "否",
    actions: "操作",
    copy: "复制",
    copied: "已复制",
    unknown: "未知",
    truncated: "结果过多，只显示了前一部分",
  },
  nav: {
    robots: "机器人",
    audit: "审计",
    users: "用户",
    groups: "分组",
    account: "账号安全",
    logout: "退出登录",
    logoutAll: "退出所有设备",
  },
  ws: {
    open: "实时已连接",
    connecting: "实时连接中…",
    closed: "实时已断开，正在重连",
    idle: "实时未连接",
  },
  login: {
    title: "登录",
    account: "邮箱或手机号",
    password: "密码",
    totp: "动态验证码",
    totpHint: "账号已开启两步验证，请输入验证器中的 6 位数字",
    invalidWithTotp: "账号、密码或验证码错误（验证码只能用一次，用过的请等下一个）",
    useRecovery: "验证器丢了？用恢复码",
    useTotp: "用验证器的动态验证码",
    recoveryCode: "恢复码",
    recoveryHint: "绑定两步验证时保存的恢复码。用它登录后两步验证会被清除，需要重新绑定",
    totpReset: "已用恢复码登录，两步验证已清除，请尽快重新绑定",
    submit: "登录",
    submitting: "登录中…",
  },
  totp: {
    title: "两步验证",
    mustSetup: "管理员账号必须先绑定两步验证才能继续使用",
    enabled: "已开启两步验证",
    disabled: "未开启两步验证",
    begin: "开始绑定",
    scan: "用验证器 App（如 Google Authenticator、Microsoft Authenticator）扫描二维码，或手动输入下面的链接",
    code: "验证器中的 6 位数字",
    confirm: "确认绑定",
    disable: "关闭两步验证",
    disableHint: "输入当前验证码以关闭",
    currentCode: "当前验证码（重新绑定时需要）",
    done: "两步验证已开启",
    off: "两步验证已关闭",
    password: "当前密码（确认是本人操作）",
    recoveryHint: "这些恢复码只显示这一次，请保存在验证器之外的地方。验证器丢失时可以用其中一个登录。",
    recoveryCopy: "复制",
    recoverySaved: "我已保存",
    recoveryRegenerate: "重新生成恢复码",
  },
  invite: {
    title: "接受邀请，设置你的密码",
    noToken: "邀请链接不完整，请重新打开管理员发给你的链接",
    org: "机构",
    account: "登录账号",
    submit: "设置密码并开通账号",
    done: "账号已开通，请用刚设置的密码登录。",
    toLogin: "去登录",
    dialogHint: "填写对方的登录账号（邮箱或手机号至少一个）。对方打开链接后自己设密码，你不会知道对方的密码。",
    nameOptional: "姓名（可选，对方可以修改）",
    generate: "生成邀请链接",
    linkHint: "链接只显示这一次，72 小时内有效，只能用一次。请通过可信渠道发给对方。",
    copy: "复制链接",
    pending: "待接受的邀请",
    revoke: "撤销",
  },
  password: {
    title: "修改密码",
    current: "当前密码",
    next: "新密码",
    confirm: "确认新密码",
    hint: "至少 10 位，不能是常见密码或账号本身。修改后其他设备会被登出",
    mustChange: "当前密码是管理员设置的，请先修改才能继续使用",
    mismatch: "两次输入的新密码不一致",
    submit: "修改密码",
    done: "密码已修改",
  },
  robots: {
    title: "机器人",
    count: (n: number) => `共 ${n} 台`,
    online: "在线",
    offline: "离线",
    onlineOnly: "只看在线",
    group: "分组",
    ungrouped: "未分组",
    searchPlaceholder: "名称或序列号",
    battery: "电量",
    charging: "充电中",
    action: "当前动作",
    controller: "控制者",
    nobody: "无人控制",
    wall: {
      title: "监控大屏",
      count: (n: number, alerts: number) => `${n} 台${alerts ? ` · ${alerts} 台有告警` : ""}`,
      alertsOnly: "只看有告警的",
      fullscreen: "全屏",
      exitFullscreen: "退出全屏",
      forms: { biped: "双足", quadruped: "四足" } as Record<string, string>,
      controlledBy: (name: string) => `${name} 在控制`,
      onboard: (state: string) => `自主：${state}`,
      truncated: (max: number, total: number) => `只显示前 ${max} 台（共 ${total} 台）：按分组筛选看其余的`,
    },
    lastSeen: "最近上报",
    status: "状态",
    disabled: "已停用",
    drive: "遥控",
    detail: "详情",
    sn: "序列号",
    model: "型号",
    edge: "边缘代理",
    firmware: "固件",
    agentMode: "Agent 模式",
    policyVersion: "策略版本",
    policyApplied: "已生效版本",
    policyPending: "待机器人确认",
    connectedAt: "本次连接",
    capabilities: "能力",
    wifi: "Wi-Fi",
    cpu: "CPU",
    mem: "内存",
    noAccess: "无权查看这台机器人的实时数据",
  },
  detail: {
    overview: "概览",
    telemetry: "遥测曲线",
    events: "事件",
    policy: "策略",
    commands: "指令记录",
    range: { "1h": "1 小时", "24h": "24 小时", "7d": "7 天", "30d": "30 天" } as Record<string, string>,
    liveEvents: "实时事件",
    historyEvents: "历史事件",
    ack: "确认",
    acked: "已确认",
    resolved: "已恢复",
    severity: { info: "信息", warning: "警告", critical: "严重" } as Record<string, string>,
    policyEffective: "生效策略",
    policyOverrides: "本机覆盖",
    maxSpeed: "最大速度",
    whitelist: "动作白名单",
    modes: "允许的模式切换",
    teaching: "教学模式",
    recording: "允许录像",
    posture: "允许远程姿态操作（变形、趴下、起立）",
    obstacleGate: "避障闸",
    obstacleGateValue: (mode: string, stop: number, slow: number) => `${mode}（${stop} cm 停、${slow} cm 减速）`,
    gateModes: { off: "关", warn: "只提示", enforce: "截停" } as Record<string, string>,
    voiceMode: "语音模式",
    readOnly: "策略编辑在 C2 提供，这里只读",
    readOnlyExceptGate: "除避障闸外的策略编辑在 C2 提供，这里只读",
    gateMode: "避障闸模式",
    gateStop: "停止距离",
    gateSaved: "已保存，等机器人确认",
    gateHint: "停止距离从机身框边缘量起；越小越能在窄处走动、转身，但离障碍越近。直行另留 15 cm、途中按速度加制动距离。",
    estop: "急停",
  },
  drive: {
    title: "遥控台",
    acquire: "申请控制",
    acquiring: "申请中…",
    release: "释放控制",
    takeover: "抢占控制",
    takeoverConfirm: (name: string) => `${name} 正在控制，确定抢占？对方会立即失去控制权。`,
    aiControls: (name: string) => `${name}正在控制（看地图、看画面不会打断它；急停随时可按）`,
    preemptAi: "接管 AI",
    preemptAiConfirm: "接管后 AI 智能体的这次会话会结束（正在做的探索、移动会停下）。确定接管？",
    youControl: "你正在控制",
    heldBy: (name: string) => `由 ${name} 控制中`,
    noLease: "未持有控制权",
    lost: "控制权已失去",
    lostReason: {
      takeover: "被其他用户抢占",
      estop: "触发了急停",
      expired: "租约过期（心跳中断）",
      permission_revoked: "权限已被收回",
      released: "已释放",
      robot_disabled: "机器人被停用",
      logout_all: "账号已在所有设备退出",
      token_reuse: "登录凭证异常，已强制退出",
      password_reset: "密码已被重置",
      password_change: "账号密码已修改",
      recovery_code: "账号已用恢复码登录",
      user_disabled: "账号已被停用",
    } as Record<string, string>,
    viewOnly: "观看模式：没有遥控权限",
    estop: "急停",
    estopHint: "空格键",
    estopSent: "急停已下发",
    estopHalt: "已急停：重新申请控制后才能继续",
    estopFailed: "急停未确认，请现场确认机器人状态",
    keyboardHint: "W/S 前后 · A/D 转向 · Q/E 横移 · 松开即停 · 空格急停",
    gear: "速度档",
    gears: ["慢", "中", "快"],
    offline: "机器人离线",
    wsDown: "实时连接已断开，已停止发送",
    stale: "实时数据中断，已停止发送",
    rejected: "服务端拒收速度指令，已停止",
    free: "当前无人控制",
    stopped: "已停止：需要先松开再操作",
    focusLost: "窗口失去焦点，已发零速",
    presets: "预设动作",
    modes: "模式切换",
    emotion: "表情",
    volume: "音量",
    tts: "说话",
    ttsPlaceholder: "输入要机器人说的话",
    send: "发送",
    sent: "已执行",
    ackTimeout: "机器人未及时回执（可能已执行）",
    noActions: "策略白名单里没有可用动作",
    joystick: "摇杆",
    robotRejected: "机器人拒绝",
    robotOffline: "机器人离线，指令未送达",
    commandFailed: "指令未送达机器人（连接拥塞或已断开）",
    // 状态栏
    status: "机器人状态",
    battery: "电量",
    charging: "充电中",
    mode: "运动模式",
    switching: "切换中",
    owner: "控制方",
    motionReady: "运动链路正常",
    owners: {
      none: "无",
      ours: "本平台",
      known: { rc: "遥控器", app_proxy: "手机 App", pnc: "自主导航", interaction: "语音交互", vr: "VR", app_proxy_t_mode: "手机 App", insta360: "Insta360 拍摄", coreon_fusion: "厂商避障" } as Record<
        string,
        string
      >,
    },
    blockers: {
      stale: "实时数据中断：以下状态可能已过时",
      noTelemetry: "还没收到机器人遥测",
      disabled: "机器人端未允许运动（edge.toml 的 allow_motion = false）",
      noSource: "运动输入源未注册（运控未就绪或刚重启，边缘会自动重试）",
      charging: "正在充电，运动被禁止",
      batteryUnknown: "电量未知，运动被禁止",
      battery: (pct: number, low: number) => `电量 ${pct}% 低于运动下限 ${low}%`,
      modeUnknown: "当前运动模式未知",
      switching: "运动模式切换中，稍候",
      mode: (label: string) => `当前模式「${label}」不能远程行走或做动作：请现场用遥控器切到双足 WBC`,
      owner: (who: string) => `${who}正在控制，运控会优先响应它（手机 App、遥控器、厂商语音的优先级都高于本平台）`,
      publish: (n: number) => `边缘发布速度失败 ${n} 次`,
    },
    // 模式切换
    modeNow: "当前",
    modeConfirm: (from: string, to: string) =>
      `从「${from}」切换到「${to}」。切换时机器人会改变姿态，请确认周围 2 米内没有人和障碍物。`,
    modeSwitch: "切换",
    modeSwitching: (to: string) => `正在切换到「${to}」…`,
    modeDone: (to: string) => `已切换到「${to}」`,
    modeUnconfirmed: (to: string, now: string) => `未确认切换到「${to}」：机器人当前为「${now}」，请现场确认`,
    modeBlocked: "灰色按钮",
    modeNoTargets: "策略里没有可远程切换的模式",
    modeHint: "只能在同一形态内直接切换；变形、趴下、起立用「姿态操作」（机器人策略开启时）或现场遥控器",
    // 实时视频
    prox: {
      title: "周围障碍",
      mute: "关闭提示音",
      unmute: "打开提示音",
      gateLabel: "平台闸",
      gate: { off: "未启用（只提示）", clear: "正常", slowdown: "减速", stop: "截停", blind: "看不见，已限速或停" } as Record<
        string,
        string
      >,
      dataLabel: "障碍数据",
      freshShort: { ok: "正常", edge_blind: "传感器无数据", link_stale: "连接延迟", none: "还没收到" } as Record<string, string>,
      fresh: {
        ok: "",
        edge_blind: "机器人的障碍传感器暂时没有数据，周围情况未知",
        link_stale: "障碍数据超过 1 秒没更新，周围情况未知",
        none: "还没收到障碍数据",
      } as Record<string, string>,
      sidesUncovered: "两侧：没有传感器",
      noObstacle: "有覆盖的方向近处没有发现障碍",
      unknown: "有覆盖的方向没有数据（可能贴得太近进了盲区），周围情况未知",
      sectors: ["前", "前左", "左", "后左", "后", "后右", "右", "前右"] as readonly string[],
      chipNone: "暂无障碍数据",
      level: { clear: "空旷", far: "较远", slow: "减速区", stop: "截停区", no_data: "无数据", uncovered: "未覆盖", stale: "过期" } as Record<
        string,
        string
      >,
      obstacleAt: (dir: string, level: string) => `${dir}方有障碍：${level}`,
      nearest: (dir: string, m: string) => `最近障碍：${dir}方 ${m} m`,
      chipFront: (m: string) => `前方障碍 ${m} m`,
      chipClear: (m: string) => `前方 ${m} m 内空旷`,
      chipUnknown: "前方无数据",
      chipStale: "障碍数据过期",
      legend: "绿 = 空旷；蓝 = 有障碍但较远；黄 = 减速区；红 = 截停区；灰色斜线 = 没有传感器或没有数据（不代表安全）。距离从机身外沿量起",
    },
    hud: {
      tilt: "俯仰",
      unknown: "未知",
      ownerOther: (who: string) => `${who}在控制`,
    },
    video: {
      title: "实时画面",
      camera: "相机",
      start: "开始观看",
      stop: "停止",
      retry: "重新连接",
      requesting: "正在请求画面…",
      waiting: "机器人正在开始推流…",
      connecting: "正在连接…",
      lost: "画面连接中断",
      noStream: "机器人没有推流（15 秒内没收到画面），请稍后重试",
      buffer: "缓冲",
      tilt: "相机俯仰",
      tiltUnknown: "未知（未设置过）",
      tiltSet: (deg: number) => `相机俯仰已设为 ${deg}°`,
      tiltPresets: { 0: "四足视角", 45: "45°", 90: "双足视角" } as Record<number, string>,
      tiltLevel: "平视",
      tiltHint: "头部相机舵机：四足形态用 0°，双足站立用 90° 看前方；左右看用摇杆原地转向。相机只朝前，身后和两侧看不到",
      pip: "画中画",
      pipHint: "同时看另一目（机器人多推一路画面）；点小画面与主画面互换",
      swap: "与主画面互换",
      fullscreen: "全屏",
      exitFullscreen: "退出全屏",
      idleHint: "点「开始观看」查看机器人相机画面",
      snapshot: "快照",
      snapshotTaking: "取帧中…",
      snapshotHint: "取当前相机的一张高清图（长边 1920）：不用开始观看，也不要控制权；图片留 1 天",
      snapshotAlt: "机器人相机快照",
      snapshotOpen: "打开原图",
      snapshotLinkTtl: "链接 10 分钟内有效（图片本身留 1 天）",
      snapshotFailed: "快照失败，请稍后再试",
      snapshotSources: { capture_jpeg: "厂商抓拍", rtsp: "拉流取帧", tap: "推流分流" } as Record<string, string>,
      snapshotErrors: {
        "media.camera_busy": "相机正在推流（可能有其他人在看），边缘没开分流取帧：所有人停止观看约 30 秒后再取",
        "media.snapshot_busy": "这台机器人正在取快照，稍等两秒再试",
        "media.snapshot_timeout": "机器人没能及时取到画面",
        "media.snapshot_unsupported": "机器人上的边缘版本不支持快照",
        "media.snapshot_failed": "机器人取帧失败",
        "robot.offline": "机器人离线",
      } as Record<string, string>,
      hint: "画面经服务器转发，不保存；双足形态下画面自动旋转 180°。没人观看约 30 秒后机器人停止推流",
      errors: {
        "media.disabled": "服务器没有配置实时视频",
        "media.unsupported": "机器人上的边缘版本不支持视频（或没有 ffmpeg）",
        "media.start_failed": "机器人没能开始推流",
        "media.unknown_camera": "未知相机",
        "robot.offline": "机器人离线",
      } as Record<string, string>,
    },
    // 姿态操作
    posture: "姿态操作",
    postureHint:
      "变形、趴下、起立动作幅度大：只在现场有人看护时使用。急停只停行走，打断不了已开始的变形，出现异常立即用遥控器接管",
    postureConfirm: (from: string, op: string) => `从「${from}」执行「${op}」。`,
    postureOnSite: "我在现场或有人在机器人旁看护（可随时用遥控器接管），周围 2 米内没有人和障碍物",
    postureFromPassive:
      "当前为卸力：机器人姿态未知。请确认它四足蜷伏在平整地面上、没有倒在一侧、没有人扶着或提着。上电后会直接发力动作",
    postureGo: "执行",
    postureRunning: (op: string) => `正在执行「${op}」…`,
    postureDone: (op: string, now: string) => `「${op}」完成，当前为「${now}」`,
    postureUnconfirmed: (op: string, now: string) => `未确认「${op}」完成：机器人当前为「${now}」，请现场确认`,
    postureBlock: {
      current: "已是这个姿态",
      unknown: "当前模式未知",
      transition: "运控正在切换",
      no_path: "当前模式不能直接执行（变四足要在双足 WBC，变双足要在四足行走，趴下/起立要在四足或卸力）",
    } satisfies Record<"current" | "unknown" | "transition" | "no_path", string>,
    modeBlock: {
      current: "已是当前模式",
      unknown: "当前模式未知",
      transition: "运控正在切换",
      no_path: "不能从当前模式直接切过去（可用姿态操作，或由现场切换）",
    } satisfies Record<"current" | "unknown" | "transition" | "no_path", string>,
    // 远程说话（C2-07）
    talk: {
      title: "远程说话",
      hold: "按住说话",
      starting: "正在打开麦克风…",
      talking: "正在说话，松开结束",
      hint: "按住按钮对着麦克风说，松开结束；声音约半秒后从机器人播出。也可以用回车键开始、再按一次结束。",
      errors: {
        insecure: "麦克风要用 https 或在本机打开控制台（当前是 http）",
        unsupported: "这个浏览器不支持录音",
        audio_failed: "没能打开录音（浏览器控制台里有原因）",
        denied: "浏览器没有允许使用麦克风",
        no_mic: "没有找到可用的麦克风",
        "media.talk_busy": "别人正在通过这台机器人说话",
        "cmd.unsupported": "机器人上的边缘版本不支持远程说话",
        "lease.required": "需要先取得控制权",
        offline: "与服务器的连接断开了",
        timeout: "机器人没有回应",
      } as Record<string, string>,
      failed: "没能开始说话",
    },
    // 声音
    sound: "声音",
    mute: "静音",
    unmute: "取消静音",
    muted: "已静音",
    volumeUnknown: "当前音量未知",
    ttsLevel: "优先级",
    ttsInterrupt: "打断正在播报的",
    // 表情与灯
    expression: "表情与灯光",
    emotionPick: "选择表情",
    emotionLoops: "播放次数",
    play: "播放",
    ledPreset: "预设灯效",
    ledPick: "选择灯效",
    ledPeriodLabel: "灯效周期",
    ledStyle: "自定义灯效样式",
    stateError: "读取设备能力失败：部分控件未显示",
    ledCustom: "自定义颜色",
    ledBreath: "呼吸",
    ledBlink: "闪烁",
    ledPeriod: (ms: number) => (ms === 0 ? "常亮" : `周期 ${(ms / 1000).toFixed(1)} 秒`),
    apply: "应用",
    neckLight: "颈部灯",
    on: "开",
    off: "关",
    notSupported: "机器人上的边缘版本不支持",
    category: {
      initial_pose: "初始姿态",
      wave: "挥手",
      handshake: "握手",
      heart: "比心",
      talk: "说话手势",
      listen: "倾听",
      think: "思考",
      greeting: "打招呼",
      hug: "拥抱",
      fist_bump: "碰拳",
      cheer: "欢呼",
      photo: "拍照",
      raise: "举手",
      pride: "骄傲",
      dance: "跳舞",
      show_off: "展示",
      skate: "滑行",
      rhythm: "律动",
      emote: "情绪",
      turn: "转身",
      high_five: "击掌",
      hold_arm: "托臂",
    } as Record<string, string>,
  },
  users: {
    title: "用户",
    create: "直接创建",
    createHint: "由你设置初始密码，对方首次登录时必须修改。不想经手密码的话用「邀请用户」。",
    invite: "邀请用户",
    name: "姓名",
    email: "邮箱",
    phone: "手机号",
    password: "初始密码",
    passwordHint: "至少 10 位，不能是常见密码或账号本身",
    role: "角色",
    roleNone: "暂不绑定",
    status: "状态",
    active: "正常",
    disabled: "已停用",
    totp: "两步验证",
    bindings: "角色绑定",
    disable: "停用",
    enable: "启用",
    resetPassword: "重置密码",
    resetHint: "重置后该用户所有设备会被登出",
    newPassword: "新密码",
    disableConfirm: (n: string) => `停用 ${n}？该用户会立即被登出。`,
  },
  bindings: {
    title: (n: string) => `${n} 的角色绑定`,
    add: "添加绑定",
    role: "角色",
    scope: "范围",
    target: "对象",
    scopeType: { org: "整个机构", robot_group: "机器人分组", robot: "单台机器人", platform: "平台" } as Record<
      string,
      string
    >,
    remove: "移除",
    none: "还没有绑定任何角色",
  },
  roles: {
    org_admin: "机构管理员",
    teacher: "教师",
    operator: "操作员",
    viewer: "观看者",
    platform_admin: "平台管理员",
  } as Record<string, string>,
  groups: {
    title: "机器人分组",
    create: "新建分组",
    name: "名称",
    parent: "上级分组",
    none: "（顶层）",
    sort: "排序",
    robots: "机器人数",
    deleteConfirm: (n: string) => `删除分组 ${n}？组内机器人会变为未分组。`,
  },
  retention: {
    title: "数据保留",
    sub: "目前只有审计记录按这里的保留期清理（每天的维护任务，先导出归档再删）；其余几项已记下，清理任务还没按它们执行",
    pickOrg: "请先在顶栏选择一个机构",
    fields: {
      audit_months: "审计记录（月）",
      telemetry_days: "原始遥测（天）",
      voice_history_days: "对话文本（天）",
      participant_retention_hours: "课堂参与者数据（小时）",
    },
    hints: {
      audit_months: (lo: number, hi: number) =>
        `${lo}~${hi} 个月；合规要求至少 180 天，到期先导出归档再删除。调短后已删除的月份调长也找不回（归档文件还在）`,
      telemetry_days: (lo: number, hi: number) => `${lo}~${hi} 天；目前平台统一保留 30 天，按机构的设置还没执行`,
      voice_history_days: (lo: number, hi: number) => `${lo}~${hi} 天；0 表示不保留对话文本`,
      participant_retention_hours: (lo: number, hi: number) => `${lo}~${hi} 小时；课堂结束后学生的匿名参与数据`,
    },
    pending: "暂未生效",
    pendingHint: "已记下，清理或执行还没按这个设置来",
    recording: "录制",
    recordingAllowed: "允许录制（实时画面与对话）",
    consentAt: (at: string) => `已于 ${at} 记录录制同意`,
    consentConfirm: "我确认已按学校规定取得录制同意（学生与教职工知情），并承担相应的告知义务",
    saved: "已保存",
    saveFailed: "保存失败，请稍后再试",
    errors: {
      "org.settings_invalid": "有一项超出允许的范围",
      "audit.recording_consent_required": "打开录制前要确认已取得同意",
      "audit.org_required": "请先在顶栏选择一个机构",
    } as Record<string, string>,
  },
  audit: {
    title: "审计",
    time: "时间",
    actor: "操作者",
    action: "操作",
    robot: "机器人",
    result: "结果",
    denied: "被拒绝",
    onlyDenied: "只看被拒绝",
    actionPrefix: "操作前缀（如 control.）",
    robotId: "机器人 ID",
    from: "开始",
    to: "结束",
    ip: "IP",
    params: "参数",
    export: {
      title: "导出",
      hint: "按上面的筛选条件导出，截止到点击的那一刻（gzip 压缩）。后台生成，文件保留 7 天，下载链接每次刷新列表重新生成；导出与下载都会记审计。已归档的月份不在其中，请看归档",
      created: "发起时间",
      format: "格式",
      status: "状态",
      rows: "条数",
      size: "大小",
      download: "下载",
      failed: "发起导出失败",
      statuses: { queued: "排队中", running: "生成中", done: "完成", failed: "失败" } as Record<string, string>,
      errors: { too_large: "太大了：请缩小时间范围再导", stalled: "后台作业没有完成，请重新导出", failed: "生成失败，请稍后重试" } as Record<string, string>,
      startErrors: { "audit.export_busy": "已有一个导出在进行中，等它完成再导", "audit.bad_range": "开始时间要早于结束时间" } as Record<string, string>,
    },
  },
  errors: {
    title: "出错了",
    forbidden: "没有权限",
    notFound: "不存在或无权查看",
    network: "网络错误，请检查连接",
    logoutIncomplete: "没能通知服务器退出。如果这是公用电脑，请联网后重新登录并退出，或清除本站 Cookie",
    newVersion: "控制台已更新，请刷新页面",
    reload: "刷新页面",
  },
};

/** 服务端错误码 → 提示。没有映射的回退到服务端 message。 */
const ERROR_TEXT: Record<string, string> = {
  "auth.invalid": "账号或密码错误",
  "auth.totp_required": "需要动态验证码",
  "auth.rate_limited": "尝试过于频繁，请稍后再试",
  "auth.totp_setup_required": "请先绑定两步验证",
  "auth.totp_required_for_admin": "管理员不能关闭两步验证",
  "auth.totp_invalid": "验证码不正确或已用过，请等下一个验证码",
  "auth.password_invalid": "当前密码不正确",
  "auth.busy": "服务器繁忙，请稍后再试",
  "auth.password_change_required": "请先修改管理员设置的密码",
  "invite.invalid": "邀请链接无效、已过期或已被使用",
  "invite.bad_ttl": "有效期不正确",
  "user.bad_name": "姓名长度不正确（1~64 个字符）",
  "authz.denied": "没有权限",
  not_found: "不存在或无权查看",
  "lease.held": "机器人正被其他用户控制",
  "lease.required": "需要先获得控制权",
  "lease.not_found": "控制权已失效",
  "robot.offline": "机器人离线",
  "robot.not_active": "机器人未启用",
  "cmd.estopped": "急停后指令已作废，请重新操作",
  "cmd.ack_timeout": "机器人未及时回执（可能已执行）",
  "cmd.rate_limited": "操作过于频繁",
  "cmd.too_many_inflight": "指令过多，请稍候",
  "cmd.out_of_policy": "策略不允许该动作",
  "cmd.system_only": "该指令只能由系统发起",
  "ai_agent.onboard_unsupported": "这台机器人的边缘版本不支持自主模式",
  "ai_agent.voice_mode": "自主模式要先把厂商语音切到 only_voice（远程运维里切换）",
  "ai_agent.no_supervisor": "现场监护要填写现场监护人",
  "ai_agent.no_rc_confirm": "请确认现场监护人带着遥控器",
  "ai_agent.unattended_unavailable": "无人值守暂未开放",
  "cmd.duplicate": "重复的请求",
  "cmd.unsupported_by_device": "设备版本不支持该指令",
  "binding.exists": "已有相同的绑定",
  "binding.last_admin": "不能移除机构的最后一个管理员",
  "binding.scope_out_of_org": "范围超出所在机构",
  "binding.scope_required": "请选择范围对象",
  "group.bad_name": "分组名称无效",
  "group.exists": "同名分组已存在",
  "group.not_empty": "分组下还有子分组",
  "group.parent_not_found": "上级分组不存在",
  "group.self_parent": "不能把自己设为上级",
  "group.too_deep": "分组最多两层",
  "role.exceeds_grantor": "不能授予超出自己权限的角色",
  "role.unknown": "角色不存在",
  "user.bad_email": "邮箱格式不正确",
  "user.bad_phone": "手机号格式不正确",
  "user.bad_status": "状态无效",
  "user.contact_required": "邮箱和手机号至少填一个",
  "user.exists": "邮箱或手机号已被使用",
  "user.self": "不能对自己执行此操作",
  "user.weak_password": "密码太弱（至少 10 位，不能是常见密码、账号本身或与当前密码相同）",
  "user.self_password": "请到「账号安全」修改自己的密码",
  "user.exceeds_actor": "对方的权限超出你的权限，不能执行此操作",
  "org.required": "请先选择机构",
  offline: "实时连接不可用",
  timeout: "等待回执超时",
};

export function errorText(code: string | undefined, fallback?: string): string {
  if (code && ERROR_TEXT[code]) return ERROR_TEXT[code];
  return fallback || code || zh.errors.title;
}
